import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { GlobalLoadingComponent } from '@/components/GlobalLoadingComponent/GlobalLoadingComponent'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { getAzureUserInformation } from '@/auth/azure/azureManager'

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
  Colors,
  ChartDataset,
} from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import { hoursToTime } from './utils'
import { Bar } from 'react-chartjs-2'
import styles from './HoursDashboard.module.scss'
import { attivita_lavorativa } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { useNavigate, useSearchParams } from 'react-router-dom'
import dragDataPlugin from 'chartjs-plugin-dragdata'
import DetailBarChart from '@/routes/hours/dashboard/DetailBarChart'
import { HourEditorFormV2 } from '@/routes/hours/HourEditor.formV2'
import { Button } from 'devextreme-react/button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useUiStore } from '@/store/uiStore'
import notify from 'devextreme/ui/notify'
import { ContextMenu } from 'devextreme-react'
import { ContextMenuRef } from 'devextreme-react/cjs/context-menu'
import { Workday } from '@/enums'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Colors,
  dragDataPlugin,
  annotationPlugin,
)

const validationRules = {}

const normalizeDateToUTCString = (date: Date) => {
  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0))
  return utcDate.toISOString()
}

function parseISODuration(d: string): number {
  const m = d.match(/PT(?:(\d+)H)?(?:(\d+)M)?/)
  if (!m) return 0
  const hrs = parseInt(m[1] || '0', 10)
  const mins = parseInt(m[2] || '0', 10)
  return hrs + mins / 60
}

// Add utility functions for activity validation
const getTotalHours = (activity: attivita_lavorativa): number => {
  return (
    parseISODuration(activity.tempo_cliente) +
    parseISODuration(activity.tempo_trasferta) +
    parseISODuration(activity.tempo_ufficio)
  )
}

const isActivityValid = (activity: attivita_lavorativa): boolean => {
  return (
    getTotalHours(activity) > 0 &&
    activity.centro_costo !== null &&
    activity.centro_costo !== undefined &&
    activity.impiegato !== null &&
    activity.impiegato !== undefined &&
    activity.data !== null &&
    activity.data !== undefined &&
    ((activity.centro_costo.causali_centro_costo &&
      activity.centro_costo.causali_centro_costo?.length > 0 &&
      activity.causale !== null &&
      activity.causale !== undefined) ||
      activity.centro_costo.causali_centro_costo?.length === 0)
  )
}

function getWorkdayFromDate(date: Date): Workday | null {
  const day = date.getDay()
  switch (day) {
    case 1:
      return Workday.Monday
    case 2:
      return Workday.Tuesday
    case 3:
      return Workday.Wednesday
    case 4:
      return Workday.Thursday
    case 5:
      return Workday.Friday
    case 6:
      return Workday.Saturday
    case 0:
      return Workday.Sunday
    default:
      return null // Should not happen
  }
}

function getISOWeekNumber(date: Date): number {
  const tempDate = new Date(date.getTime())
  tempDate.setHours(0, 0, 0, 0)
  // Thursday in current week decides the year
  tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7))
  const week1 = new Date(tempDate.getFullYear(), 0, 4)
  return 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
}

const generateColor = (str: string): string => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
    hash |= 0 // Ensure 32-bit integer
  }

  const hue = hash % 360
  const saturation = 70 // Use a fixed saturation for consistency
  const lightness = 65 // Use a fixed lightness for pastel-like colors

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`
}

const useAppointments = (weekDate: Date) => {
  const qsAdminApiManager = useQsAdminApiManager()

  return useQuery({
    queryKey: ['hours', normalizeDateToUTCString(weekDate)],
    queryFn: async () => {
      const currentUser = await getAzureUserInformation()
      const mail = currentUser?.email
      if (!mail) {
        throw new Error('User email not found')
      }
      const firstWeekDay = new Date(weekDate)
      const lastWeekDay = new Date(firstWeekDay)
      lastWeekDay.setDate(firstWeekDay.getDate() + 6)

      // Normalize both dates to ensure consistent UTC formatting
      const startDate = normalizeDateToUTCString(firstWeekDay)
      const endDate = normalizeDateToUTCString(lastWeekDay)

      const response = await qsAdminApiManager.service.attivita_lavorativa().query((builder, qatt_lav) => {
        builder.filter(
          qatt_lav.impiegato.props.email
            .eq(mail)
            .and(qatt_lav.data.greaterEquals(startDate).and(qatt_lav.data.le(endDate))),
        )
        builder.expand('impiegato', 'task', 'causale', 'intervento', 'piattaforma')
        builder.expanding('centro_costo', (qCentroCosto) => {
          qCentroCosto.expanding('causali_centro_costo', (qCausali) => {
            qCausali.select('id', 'nome')
          })
        })
        builder.expanding('sede', (qSede) => {
          qSede.expanding('azienda', (qAzienda) => {
            qAzienda.select('id', 'nome')
          })
        })
      })

      return response.data.value
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

const useHolidays = (currentWeek: Date) => {
  return useQuery({
    queryKey: ['holidays', currentWeek.getFullYear()],
    queryFn: async () => {
      const year = currentWeek.getFullYear()
      const countryCode = 'IT' // Italy
      const response = await fetch(`https://date.nager.at/api/v3/PublicHolidays/${year}/${countryCode}`)
      if (!response.ok) {
        throw new Error('Failed to fetch holidays')
      }
      const holidays: { date: string; global?: boolean }[] = await response.json()

      return holidays.filter((holiday) => holiday.global !== false)
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - holidays don't change often
    gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days
  })
}

const isoDurationToTimespan = (iso: string): string => {
  if (!iso || !iso.startsWith('PT')) {
    return '0.00:00:00'
  }
  const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?/)
  if (!m) return '0.00:00'
  const hrs = parseInt(m[1] || '0', 10)
  const mins = parseInt(m[2] || '0', 10)

  const hoursStr = hrs.toString().padStart(2, '0')
  const minutesStr = mins.toString().padStart(2, '0')

  return `0.${hoursStr}:${minutesStr}:00`
}

export const HoursDashboard = () => {
  const weekParam = useSearchParams()[0].get('week')
  const [currentWeek, setCurrentWeek] = useState<Date>(weekParam ? new Date(weekParam) : getStartOfWeek(new Date()))
  const chartRef = useRef<ChartJS<'bar'>>(null)
  const [chartData, setChartData] = useState<ChartData<'bar'>>()
  const navigate = useNavigate()
  const [selectedAttivita, setSelectedAttivita] = useState<attivita_lavorativa | null>(null)
  const { data: appointments, isLoading, error, refetch } = useAppointments(currentWeek)
  const [dirtyAppointments, setDirtyAppointments] = useState<Record<number, attivita_lavorativa>>({})
  const queryClient = useQueryClient()
  const qsAdminApiManager = useQsAdminApiManager()
  const [isCreating, setIsCreating] = useState(false)
  const { data: holidays, isLoading: isHolidaysLoading } = useHolidays(currentWeek)
  const { hoursView, setHoursView } = useUiStore()
  const [isFormExpanded, setIsFormExpanded] = useState(false)
  const chartContextMenuRef = useRef<ContextMenuRef>(null)

  // Calculate festive days from holidays data
  const festiveDays = useMemo(() => {
    if (!holidays) return []

    const weekStart = new Date(currentWeek)
    const weekEnd = new Date(currentWeek)
    weekEnd.setDate(weekEnd.getDate() + 6)

    return holidays
      .map((holiday) => new Date(holiday.date))
      .filter((holidayDate) => holidayDate >= weekStart && holidayDate <= weekEnd)
      .map((holidayDate) => {
        const dayOfWeek = holidayDate.getDay() // Sunday: 0, Monday: 1, ..., Saturday: 6
        if (dayOfWeek >= 1 && dayOfWeek <= 5) {
          // Monday to Friday
          return dayOfWeek - 1 // Map to 0-4 index
        }
        return -1
      })
      .filter((index) => index !== -1)
  }, [holidays, currentWeek])

  // Function to create annotations for the chart
  const createAnnotations = useCallback(() => {
    const annotations: Record<string, Record<string, unknown>> = {}

    // Add weekend annotations
    const weekendIndices = [5, 6] // Saturday, Sunday
    weekendIndices.forEach((dayIndex) => {
      annotations[`weekend-${dayIndex}`] = {
        type: 'box',
        xMin: dayIndex - 0.5,
        xMax: dayIndex + 0.5,
        yMin: 0,
        yMax: 10,
        backgroundColor: 'rgba(255, 115, 0, 0.05)',
        borderColor: 'transparent',
      }
    })

    // Add festive days annotations
    festiveDays.forEach((dayIndex) => {
      annotations[`festive-${dayIndex}`] = {
        type: 'box',
        xMin: dayIndex - 0.5,
        xMax: dayIndex + 0.5,
        yMin: 0,
        yMax: 10,
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        borderColor: 'rgba(255, 99, 132, 0.3)',
        borderWidth: 0,
        label: {
          display: true,
          content: '🎉 Festivo',
          position: 'center',
          font: {
            size: 12,
          },
          color: 'rgba(255, 99, 132, 0.8)',
        },
      }
    })

    // Add creation placeholder annotation
    if (isCreating && selectedAttivita?.data) {
      const activityDate = new Date(selectedAttivita.data)
      const dayOfWeek = activityDate.getDay()
      const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1 // Monday is 0, Sunday is 6

      if (dayIndex >= 0 && dayIndex < 7) {
        annotations['creation-placeholder'] = {
          type: 'box',
          xMin: dayIndex - 0.4,
          xMax: dayIndex + 0.4,
          yMin: 0,
          yMax: 1,
          backgroundColor: 'rgba(54, 162, 235, 0.1)',
          borderColor: 'rgba(54, 162, 235, 0.5)',
          borderWidth: 3,
          borderDash: [5, 5],
          label: {
            display: true,
            content: '➕ Nuova Attività',
            position: 'center',
            font: {
              size: 12,
              weight: 'bold',
            },
            color: 'rgba(54, 162, 235, 0.8)',
          },
        }
      }
    }

    // Add selection highlight annotation
    if (selectedAttivita && selectedAttivita.data && !isCreating && chartData) {
      const activityDate = new Date(selectedAttivita.data)
      const dayOfWeek = activityDate.getDay()
      const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      const activityLabel = selectedAttivita.ded_Dis || 'Unknown'

      let yMin = 0
      let yMax = 0

      for (const dataset of chartData.datasets) {
        const value = dataset.data[dayIndex] as number
        if (dataset.label === activityLabel) {
          yMax = yMin + value
          break
        }
        yMin += value
      }

      if (yMax > yMin) {
        annotations['selection-highlight'] = {
          type: 'box',
          xMin: dayIndex - 0.4,
          xMax: dayIndex + 0.4,
          yMin,
          yMax,
          backgroundColor: 'transparent',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 3,
        }
      }
    }

    return annotations
  }, [festiveDays, isCreating, selectedAttivita, chartData])

  const saveAppointmentMutation = useMutation({
    mutationFn: (updatedAppointment: attivita_lavorativa) => {
      // Additional validation before sending to API
      if (!isActivityValid(updatedAppointment)) {
        throw new Error('Cannot save invalid activity')
      }

      const appointmentForApi = {
        ...updatedAppointment,
        tempo_cliente: isoDurationToTimespan(updatedAppointment.tempo_cliente),
        tempo_trasferta: isoDurationToTimespan(updatedAppointment.tempo_trasferta),
        tempo_ufficio: isoDurationToTimespan(updatedAppointment.tempo_ufficio),
      }

      const { id, ...creationData } = appointmentForApi
      return id && id > 0
        ? qsAdminApiManager.service.attivita_lavorativa(id).update(appointmentForApi)
        : qsAdminApiManager.service.attivita_lavorativa().create(creationData)
    },
    onMutate: async (updatedAppointment: attivita_lavorativa) => {
      const queryKey = ['hours', currentWeek.toISOString()]
      await queryClient.cancelQueries({ queryKey })
      const previousAppointments = queryClient.getQueryData<attivita_lavorativa[]>(queryKey)
      if (previousAppointments && !isCreating) {
        const newAppointments = previousAppointments.map((app) =>
          app.id === updatedAppointment.id ? updatedAppointment : app,
        )
        queryClient.setQueryData(queryKey, newAppointments)
      }
      return { previousAppointments }
    },
    onSuccess: (updatedAppointment) => {
      notify({
        message: 'Attività salvata con successo',
        type: 'success',
      })
    },
    onError: (err, updatedAppointment, context) => {
      const errorMessage = err.message.includes('zero total time')
        ? 'Impossibile salvare attività con 0 ore totali'
        : "Errore durante il salvataggio dell'attività"

      notify({
        message: errorMessage,
        type: 'error',
      })
      if (context?.previousAppointments) {
        queryClient.setQueryData(['hours', normalizeDateToUTCString(currentWeek)], context.previousAppointments)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['hours', normalizeDateToUTCString(currentWeek)] })
      setIsCreating(false)
    },
  })

  const deleteAppointmentMutation = useMutation({
    mutationFn: (activityId: number) => {
      return qsAdminApiManager.service.attivita_lavorativa(activityId).delete()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hours', normalizeDateToUTCString(currentWeek)] })
      setSelectedAttivita(null)
      setIsCreating(false)
    },
    onError: (error) => {
      console.error('Error deleting activity', error)
      // Optionally, show a notification to the user
      notify({
        message: "Errore durante la cancellazione dell'attività",
        type: 'error',
      })
    },
  })

  const handleSave = () => {
    // Filter out invalid activities and show warning if any exist
    const validActivities = Object.values(dirtyAppointments).filter(isActivityValid)
    const invalidActivities = Object.values(dirtyAppointments).filter((activity) => !isActivityValid(activity))

    if (invalidActivities.length > 0) {
      notify({
        message: `${invalidActivities.length} attività non valide non verranno salvate`,
        type: 'warning',
      })

      // Remove invalid activities from dirty state
      const newDirtyAppointments = { ...dirtyAppointments }
      invalidActivities.forEach((activity) => {
        delete newDirtyAppointments[activity.id]
        // If it's a new activity being created, also clear the selection
        if (activity.id < 0 && selectedAttivita?.id === activity.id) {
          setSelectedAttivita(null)
          setIsCreating(false)
        }
      })
      setDirtyAppointments(newDirtyAppointments)
    }

    if (validActivities.length === 0) {
      notify({
        message: 'Nessuna attività valida da salvare',
        type: 'info',
      })
      setIsFormExpanded(false)
      return
    }

    validActivities.forEach((appointment) => {
      saveAppointmentMutation.mutate(appointment)
    })
    setDirtyAppointments({})
    setIsFormExpanded(false)
    setSelectedAttivita(null)
  }

  const handleCancel = () => {
    setDirtyAppointments({})
    setIsFormExpanded(false)
    setSelectedAttivita(null)
    setIsCreating(false)
    refetch()
  }

  const handleDelete = (activityId: number) => {
    if (activityId > 0) {
      deleteAppointmentMutation.mutate(activityId)
    } else {
      // It's a new, unsaved activity. Just remove it from local state.
      setDirtyAppointments((prev) => {
        const newDirty = { ...prev }
        delete newDirty[activityId]
        return newDirty
      })
      setSelectedAttivita(null)
      setIsFormExpanded(false)
      setIsCreating(false)
    }
  }

  const handleHourChange = (updatedAppointment: attivita_lavorativa) => {
    setSelectedAttivita(updatedAppointment)
    const key = updatedAppointment.id
    if (!isActivityValid(updatedAppointment) && updatedAppointment.id > 0) {
      setDirtyAppointments((prev) => {
        const newDirty = { ...prev }
        delete newDirty[key]
        return newDirty
      })
    } else {
      // Add to dirty appointments (both valid activities and new activities with zero time)
      setDirtyAppointments((prev) => ({
        ...prev,
        [key]: updatedAppointment,
      }))
    }
  }

  const formatChartData = useCallback(
    (appointments: attivita_lavorativa[]): ChartData<'bar'> => {
      // Create a color map for centro_costo
      const centroCostoColorMap: Record<string, string> = {}
      appointments.forEach((activity) => {
        const centroCostoName = activity.centro_costo?.nome || 'Nessun Centro di Costo'
        if (!centroCostoColorMap[centroCostoName]) {
          centroCostoColorMap[centroCostoName] = generateColor(centroCostoName)
        }
      })

      // Group activities by their label (activity type)
      const activitiesByLabel: Record<
        string,
        { day: Workday; hours: number; id: number; centro_costo_name: string }[]
      > = {}

      appointments.forEach((activity) => {
        if (!activity.data) return
        const date = new Date(activity.data)
        const workday = getWorkdayFromDate(date)
        if (!workday) return

        const label = activity.ded_Dis || (activity.id < 0 ? `new_activity_${activity.id}` : 'Unknown')
        const hours =
          parseISODuration(activity.tempo_cliente) +
          parseISODuration(activity.tempo_trasferta) +
          parseISODuration(activity.tempo_ufficio)
        const centro_costo_name = activity.centro_costo?.nome || 'Nessun Centro di Costo'

        if (!activitiesByLabel[label]) {
          activitiesByLabel[label] = []
        }

        activitiesByLabel[label].push({ day: workday, hours, id: activity.id, centro_costo_name })
      })

      // Convert grouped activities to Chart.js datasets
      const datasets = Object.entries(activitiesByLabel).map(([label, activities]) => {
        // Initialize data array with zeros for all weekdays
        const data = Object.values(Workday).map(() => 0)

        // Fill in hours for days that have activities
        activities.forEach(({ day, hours }) => {
          const dayIndex = Object.values(Workday).indexOf(day)
          if (dayIndex !== -1) {
            data[dayIndex] += hours // Add hours to correct day index
          }
        })

        const backgroundColor = centroCostoColorMap[activities[0].centro_costo_name]

        const dataset: ChartDataset<'bar'> = {
          label,
          data,
          barPercentage: 0.9,
          categoryPercentage: 0.9,
          backgroundColor,
          borderColor: 'rgba(0, 0, 0, 0.2)',
          borderWidth: 1,
          // Store the ID of the first activity of this type. This is a simplification.
          // If multiple activities can have the same ded_Dis, this needs a more robust solution.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...(activities[0]?.id && { activityId: activities[0]?.id as any }),
        }

        return dataset
      })
      const labels: any[] = []
      const startDate = weekParam ? new Date(weekParam) : getStartOfWeek(new Date())
      for (let index = 0; index < 7; index++) {
        const label = getWorkdayFromDate(startDate)
        labels.push(label + ' ' + startDate.getDate().toString())
        startDate.setDate(startDate.getDate() + 1)
      }
      return {
        labels: labels,
        datasets,
      }
    },
    [weekParam],
  )

  useEffect(() => {
    if (appointments) {
      const updatedAppointments = [...appointments]
      const dirtyValues = Object.values(dirtyAppointments)

      dirtyValues.forEach((dirtyApp) => {
        const index = updatedAppointments.findIndex((app) => app.id === dirtyApp.id)
        if (index !== -1) {
          updatedAppointments[index] = dirtyApp
        } else {
          updatedAppointments.push(dirtyApp)
        }
      })

      const formattedData = formatChartData(updatedAppointments)
      setChartData(formattedData)
    }
  }, [appointments, dirtyAppointments, formatChartData, isCreating])

  // Update chart annotations when creation day or festive days change
  useEffect(() => {
    const chart = chartRef.current
    if (chart && chart.options.plugins?.annotation) {
      chart.options.plugins.annotation.annotations = createAnnotations()
      chart.update('none') // Update without animation for better performance
    }
  }, [createAnnotations])

  // Gets Monday of the current week
  function getStartOfWeek(date: Date): Date {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1) // Adjust when day is Sunday
    return new Date(d.setDate(diff))
  }

  // Format date as DD/MM/YYYY
  function formatDate(date: Date): string {
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${date.getFullYear()}`
  }
  // Navigate to previous week
  const goToPreviousWeek = () => {
    setDirtyAppointments({}) // Clear dirty appointments
    setSelectedAttivita(null)
    setCurrentWeek((prev) => {
      // Create a new date and normalize it to avoid timezone issues
      const newDate = new Date(prev.getTime())
      newDate.setDate(newDate.getDate() - 7)
      // Normalize to start of day to ensure consistent time
      newDate.setHours(0, 0, 0, 0)
      navigate(`?week=${normalizeDateToUTCString(newDate)}`)
      return newDate
    })
  }

  // Navigate to next week
  const goToNextWeek = () => {
    setDirtyAppointments({}) // Clear dirty appointments
    setSelectedAttivita(null)
    setCurrentWeek((prev) => {
      // Create a new date and normalize it to avoid timezone issues
      const newDate = new Date(prev.getTime())
      newDate.setDate(newDate.getDate() + 7)
      // Normalize to start of day to ensure consistent time
      newDate.setHours(0, 0, 0, 0)
      navigate(`?week=${normalizeDateToUTCString(newDate)}`)
      return newDate
    })
  }

  // Go to current week
  const goToCurrentWeek = () => {
    setDirtyAppointments({}) // Clear dirty appointments
    setSelectedAttivita(null)
    const newDate = getStartOfWeek(new Date())
    // Normalize to start of day to ensure consistent time
    newDate.setHours(0, 0, 0, 0)
    setCurrentWeek(newDate)
    navigate(`?week=${normalizeDateToUTCString(newDate)}`)
  }

  // Chart.js options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      colors: {
        enabled: false, // Disable automatic coloring
      },
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title(tooltipItems) {
            return tooltipItems[0].label // Day name
          },
          label(tooltipItem) {
            const dataset = tooltipItem.dataset
            const dataIndex = tooltipItem.dataIndex
            const value = dataset.data[dataIndex]
            return `${hoursToTime(value)} (${dataset.label})`
          },
          footer(tooltipItems) {
            const itemDed_dis = tooltipItems[0].dataset.label
            return `Note: ${appointments?.find((a) => a.ded_Dis === itemDed_dis)?.note || 'N/A'}`
          },
        },
      },
      dragData: {
        dragX: false,
        dragY: false,
      },
      annotation: {
        annotations: createAnnotations(),
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        stacked: true,
        title: { display: true, text: 'Ore' },
        suggestedMax: 10,
        suggestedMin: 0,
      },
      x: {
        stacked: true,
        title: { display: true, text: 'Giorno' },
      },
    },
    onClick: (e, elements) => {
      const chart = chartRef.current
      if (!chart || !e.x || !e.native) return

      const canvas = chart.canvas
      const rect = canvas.getBoundingClientRect()
      const x = e.x - rect.left

      // Instead of complex bin calculations, use Chart.js's built-in nearest item finder
      // This will properly align with how Chart.js positions the bars
      const items = chart.getElementsAtEventForMode(e.native, 'nearest', { axis: 'x', intersect: false }, false)

      // If we found an item, use its index directly
      let dayIndex = -1
      if (items.length > 0) {
        // This gets the index of the nearest bar, which is what we want
        dayIndex = items[0].index
        console.log(`Clicked near index: ${dayIndex}`)
      } else {
        // Fallback to manual calculation if nearest fails
        const xScale = chart.scales.x
        const clickedValue = xScale.getValueForPixel(x)

        if (clickedValue === undefined) return

        // For categorical axes, round to nearest integer since categories are at integer positions
        // Add a tiny bias (0.01) to handle clicks exactly at the boundary
        dayIndex = Math.round(clickedValue + 0.01)
        console.log(`Clicked calculated index: ${dayIndex}`)
      }

      // Validate the day index is within the valid range
      if (dayIndex >= 0 && dayIndex < Object.values(Workday).length) {
        const clickedDate = new Date(currentWeek)
        clickedDate.setHours(12, 0, 0, 0) // Normalize time to avoid timezone issues
        clickedDate.setDate(currentWeek.getDate() + dayIndex)

        if (elements.length === 0) {
          // Check if the clicked day is a festive day
          if (festiveDays.includes(dayIndex)) {
            console.log('Cannot create activity on a festive day.')
            return // Prevent creation
          }
          const dayName = Object.values(Workday)[dayIndex]

          if (dayName) {
            const newActivityId = Math.min(...Object.keys(dirtyAppointments).map(Number), 0) - 1
            const newActivity = {
              id: newActivityId,
              data: clickedDate.toISOString(),
              tempo_cliente: 'PT0H0M',
              tempo_trasferta: 'PT0H0M',
              tempo_ufficio: 'PT0H0M',
              ded_Dis: `new_activity_${newActivityId}`, // Use a unique temporary label
              ded_Id: null,
              ded_RootFam: '',
              note: '',
              spese_alloggio: 0,
              spese_trasferta: 0,
              spese_vitto: 0,
            } as attivita_lavorativa
            setIsCreating(true)
            setSelectedAttivita(newActivity)
            setDirtyAppointments((prev) => ({ ...prev, [newActivityId]: newActivity }))
          }
        } else {
          setIsCreating(false)
          const element = elements[0]
          const datasetIndex = element.datasetIndex
          const datasetLabel = chartData?.datasets[datasetIndex]?.label

          const allAppointments = [...(appointments || []), ...Object.values(dirtyAppointments)]
          const clickedActivity = allAppointments.find((a) => {
            if (!a.data) return false
            const activityDate = new Date(a.data)
            const activityLabel = a.ded_Dis || (a.id < 0 ? `new_activity_${a.id}` : 'Unknown')

            const isSameDay =
              activityDate.getFullYear() === clickedDate.getFullYear() &&
              activityDate.getMonth() === clickedDate.getMonth() &&
              activityDate.getDate() === clickedDate.getDate()

            return activityLabel === datasetLabel && isSameDay
          })

          if (clickedActivity) {
            setSelectedAttivita(dirtyAppointments[clickedActivity.id] || clickedActivity)
          } else {
            setSelectedAttivita(null)
          }
        }
      }
    },
  } satisfies ChartOptions<'bar'>
  // Calculate end of week date for display
  const endOfWeek = new Date(currentWeek)
  endOfWeek.setDate(endOfWeek.getDate() + 6) // Sunday is 6 days after Monday

  const chartOnContextMenu = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    const chart = chartRef.current
    if (!chart || !e.nativeEvent || !e.nativeEvent.offsetX) return

    const canvas = chart.canvas
    const rect = canvas.getBoundingClientRect()
    const x = e.nativeEvent.offsetX - rect.left

    // Instead of complex bin calculations, use Chart.js's built-in nearest item finder
    // This will properly align with how Chart.js positions the bars
    const items = chart.getElementsAtEventForMode(e.nativeEvent, 'nearest', { axis: 'x', intersect: false }, false)

    // If we found an item, use its index directly
    let dayIndex = -1
    if (items.length > 0) {
      // This gets the index of the nearest bar, which is what we want
      dayIndex = items[0].index
      console.log(`Clicked near index: ${dayIndex}`)
    } else {
      // Fallback to manual calculation if nearest fails
      const xScale = chart.scales.x
      const clickedValue = xScale.getValueForPixel(x)

      if (clickedValue === undefined) return

      // For categorical axes, round to nearest integer since categories are at integer positions
      // Add a tiny bias (0.01) to handle clicks exactly at the boundary
      dayIndex = Math.round(clickedValue + 0.01)
      console.log(`Clicked calculated index: ${dayIndex}`)
    }

    // Validate the day index is within the valid range
    if (dayIndex >= 0 && dayIndex < Object.values(Workday).length) {
      const clickedDate = new Date(currentWeek)
      clickedDate.setHours(12, 0, 0, 0) // Normalize time to avoid timezone issues
      clickedDate.setDate(currentWeek.getDate() + dayIndex)

      const elements = chart.getActiveElements()
      if (elements.length === 0) {
        if (selectedAttivita && selectedAttivita.id > 0) setSelectedAttivita(null)
        e.preventDefault()
      } else {
        setIsCreating(false)
        const element = elements[0]
        const datasetIndex = element.datasetIndex
        const datasetLabel = chartData?.datasets[datasetIndex]?.label

        const allAppointments = [...(appointments || []), ...Object.values(dirtyAppointments)]
        const clickedActivity = allAppointments.find((a) => {
          if (!a.data) return false
          const activityDate = new Date(a.data)
          const activityLabel = a.ded_Dis || (a.id < 0 ? `new_activity_${a.id}` : 'Unknown')

          const isSameDay =
            activityDate.getFullYear() === clickedDate.getFullYear() &&
            activityDate.getMonth() === clickedDate.getMonth() &&
            activityDate.getDate() === clickedDate.getDate()

          return activityLabel === datasetLabel && isSameDay
        })

        if (clickedActivity) {
          setSelectedAttivita(dirtyAppointments[clickedActivity.id] || clickedActivity)
        } else {
          setSelectedAttivita(null)
        }
      }
    }
  }

  const onDuplicaClickChartContextMenu = () => {
    if (selectedAttivita && selectedAttivita.id > 0) {
      console.log('duplica')
      const now: Date = new Date()
      const newActivityId = Math.min(...Object.keys(dirtyAppointments).map(Number), 0) - 1
      const newActivity = {
        id: newActivityId,
        data: now.toISOString(),
        tempo_cliente: 'PT0H0M',
        tempo_trasferta: 'PT0H0M',
        tempo_ufficio: 'PT0H0M',
        ded_Dis: `new_activity_${newActivityId}`, // Use a unique temporary label
        ded_Id: null,
        ded_RootFam: '',
        note: selectedAttivita?.note,
        spese_alloggio: 0,
        spese_trasferta: 0,
        spese_vitto: 0,
        impiegato: selectedAttivita?.impiegato,
        centro_costo: selectedAttivita?.centro_costo,
        piattaforma: selectedAttivita?.piattaforma,
        causale: selectedAttivita?.causale,
        task: selectedAttivita?.task,
        intervento: selectedAttivita?.intervento,
        sede: selectedAttivita?.sede,
      } as attivita_lavorativa
      console.log('new activity', newActivity)
      setIsCreating(true)
      setSelectedAttivita(newActivity)
      setDirtyAppointments((prev) => ({ ...prev, [newActivityId]: newActivity }))
    } else notify('Nessuna attività da duplicare', 'warning')
  }

  const onDeleteClickChartContextMenu = () => {
    if (selectedAttivita && selectedAttivita.id > 0) handleDelete(selectedAttivita.id)
    else notify('Nessuna attività da eliminare', 'warning')
  }

  // Handle loading state
  if (isLoading || isHolidaysLoading) {
    return <GlobalLoadingComponent />
  }

  // Handle error state
  if (error) {
    return (
      <div className={styles.hoursDashboard}>
        <h2>Dashboard Ore Settimanali</h2>
        <div className={styles.errorMessage}>
          <p>Errore nel caricamento dei dati: {error.message}</p>
          <button onClick={() => window.location.reload()}>Riprova</button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`content-block responsive-paddings ${styles.hoursDashboard} ${
        isFormExpanded ? styles.formExpanded : ''
      }`}
    >
      <div className={`${styles.weekSection}`}>
        <div className={styles.topBar}>
          <div className={styles.topBarLeft}>
            <Button
              onClick={() => (selectedAttivita ? setIsFormExpanded((prev) => !prev) : null)}
              stylingMode="text"
              type="default"
              hint={isFormExpanded ? 'Riduci' : 'Espandi'}
            >
              <FontAwesomeIcon icon={['fas', isFormExpanded ? 'compress-alt' : 'expand-alt']} />
            </Button>
            <Button
              text="Vecchia Griglia"
              className={styles.navButton}
              onClick={() => {
                setHoursView(hoursView === 'dashboard' ? 'grid' : 'dashboard')
                navigate('/workactivities/hours')
              }}
              stylingMode="outlined"
              type="default"
            />
            <Button
              text="Oggi"
              className={styles.navButton}
              onClick={goToCurrentWeek}
              stylingMode="outlined"
              type="default"
            />
            <Button
              className={styles.navButton}
              onClick={goToPreviousWeek}
              stylingMode="text"
              type="default"
              render={() => <FontAwesomeIcon icon={['fas', 'less-than']} />}
            />
            <Button
              className={styles.navButton}
              onClick={goToNextWeek}
              stylingMode="text"
              type="default"
              render={() => <FontAwesomeIcon icon={['fas', 'greater-than']} />}
            />
          </div>
          <div className={styles.topBarCenter}>
            <div className={styles.weekDisplay}>
              <strong>
                {currentWeek.getDate()}-{endOfWeek.getDate()} {endOfWeek.toLocaleDateString('it-IT', { month: 'long' })}{' '}
                {currentWeek.getFullYear()}{' '}
              </strong>
              (Settimana {getISOWeekNumber(currentWeek)})
            </div>
          </div>
          <div className={styles.topBarRight}>
            {Object.keys(dirtyAppointments).length > 0 && (
              <>
                <Button text="Annulla" onClick={handleCancel} type="normal" stylingMode="outlined" />
                <Button text="Salva" onClick={handleSave} type="success" stylingMode="contained" />
              </>
            )}
          </div>
        </div>

        {chartData && (
          <>
            <div className={`${styles.chartContainer} ${isFormExpanded ? styles.hidden : ''}`}>
              <Bar id="bar" ref={chartRef} data={chartData} options={options} onContextMenu={chartOnContextMenu} />
            </div>
            <ContextMenu
              ref={chartContextMenuRef}
              target="#bar"
              items={[
                {
                  text: 'Duplica',
                  icon: 'copy',
                  visible: selectedAttivita && selectedAttivita.id > 0 ? true : false,
                  onClick: onDuplicaClickChartContextMenu,
                },
                {
                  text: 'Elimina',
                  icon: 'trash',
                  visible: selectedAttivita && selectedAttivita.id > 0 ? true : false,
                  onClick: onDeleteClickChartContextMenu,
                },
              ]}
              height={'auto'}
              width={'auto'}
            />
          </>
        )}
      </div>
      {selectedAttivita && (
        <>
          <div className={`responsive-paddings ${styles.detailSectionChart} ${isFormExpanded ? styles.hidden : ''}`}>
            <DetailBarChart
              attivita={selectedAttivita}
              onUpdate={handleHourChange}
              onClose={() => setSelectedAttivita(null)}
            />
          </div>
          <div className={`responsive-paddings ${styles.detailSectionForm} ${isFormExpanded ? styles.expanded : ''}`}>
            <HourEditorFormV2
              key={selectedAttivita?.id}
              hour={selectedAttivita}
              onHourChange={handleHourChange}
              onDelete={handleDelete}
              creating={isCreating}
              isExpanded={isFormExpanded}
              onToggleExpand={() => (selectedAttivita ? setIsFormExpanded((prev) => !prev) : null)}
            />
          </div>
        </>
      )}
    </div>
  )
}
